import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAuth } from 'react-oidc-context';
import { APP_SUPPORTS_MULTI_LANGUAGE } from '@constants/app';
import { useSearchParams } from 'react-router-dom';
import { useDataProvider } from '../DataProvider';
import PageLoading from '../../components/UI/PageLoading';
import { useStore } from '../MobxProvider';
import { useUserAccess } from '../UserAccessProvider';

export const useMasterData = () => {
  const store = useStore();
  const storeItems = store.domain.masterData.get();
  return storeItems || {};
};

function AppDataProvider({
  children,
  metaDataUrl,
  getInjectableAdditionalMetaData = null,
}) {
  const store = useStore();
  const { user: { session_stat: sessionState = null } = {} } = useAuth() || {};
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);
  const userAcces = useUserAccess();

  const [searchParams, setSearchParams] = useSearchParams();
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(false);

  const masterData = useMasterData();

  const currentSession = store.domain.masterData.getSession();

  const cleanKeycloakParams = () => {
    const paramsToDelete = ['state', 'code', 'session_state'];
    let changed = false;

    paramsToDelete.forEach(param => {
      if (searchParams.has(param)) {
        searchParams.delete(param);
        changed = true;
      }
    });

    if (changed) {
      // Only update if at least one param was deleted
      setSearchParams(searchParams.toString(), { replace: true });
    }
  };

  const fetchAndSetMetaData = async () => {
    const data = await get(`${getApiUrl()}${metaDataUrl}`);
    if (data?.ok) {
      if (
        Array.isArray(data?.data?.languages) &&
        data?.data?.languages?.length > 0
      ) {
        store.domain.locale.setSupportedLanguages(data?.data?.languages);
      } else if (APP_SUPPORTS_MULTI_LANGUAGE) {
        store.domain.locale.setSupportedLanguages(['en', 'es']);
      }

      const newmMasterData = data?.data;
      if (typeof getInjectableAdditionalMetaData === 'function') {
        const injectableData = await getInjectableAdditionalMetaData(userAcces);
        newmMasterData.frontendAppData = injectableData;
      }
      store.domain.masterData.set(newmMasterData);
    } else {
      store.domain.masterData.set({});
      store.domain.locale.setSupportedLanguages([]);
      throw new Error('Config fetch failed');
    }
  };

  const fetchAppData = async (forceReFetch = false) => {
    try {
      const hasCachedMasterData = Object.entries(masterData)?.length > 0;

      if (!hasCachedMasterData || forceReFetch) {
        await fetchAndSetMetaData();
      }

      setError(false);
    } catch (e) {
      setError(true);
    }
    setInitializing(false);
  };

  useEffect(() => {
    /*
     * Refetch logic will execute if user logs in
     */
    const refetchData = !currentSession || currentSession !== sessionState;

    if (refetchData) {
      store.domain.masterData.setSession(sessionState);
    }

    fetchAppData(refetchData);
  }, []);

  useEffect(() => {
    cleanKeycloakParams();
  }, []);

  if (initializing) {
    return (
      <PageLoading>
        <FormattedMessage
          id="LOAD_MASTER_DATA"
          defaultMessage="Loading Master Data"
        />
      </PageLoading>
    );
  }

  if (error) {
    return <p>Error fetching master data</p>;
  }

  return children;
}

export default AppDataProvider;
